import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { XRControllerModelFactory } from 'three/addons/webxr/XRControllerModelFactory.js';
import { XRHandModelFactory } from 'three/addons/webxr/XRHandModelFactory.js';
import { Text } from 'troika-three-text';
import { gsap } from 'gsap';
import GameMover from '../GameMover.js';
import * as SkeletonUtils from 'three/addons/utils/SkeletonUtils.js';
import { SpacePiratesPhysics, COLLISION_GROUPS } from './SpacePiratesPhysics.js';
import { WallManager } from './SpacePirateProceduralWorld.js';
import * as CANNON from 'cannon-es';
import AudioManager from './AudioManager.js';

const GameState = {
    MENU: 'MENU',
    PLAYING: 'PLAYING',
    GAME_OVER: 'GAME_OVER'
};



class BulletManager {
    constructor(scene, physics, config, playerRig) {
        this.scene = scene;
        this.physics = physics;
        this.config = config;
        this.playerRig = playerRig;
        this.bullets = [];
        this.textureLoader = new THREE.TextureLoader();
        this.bulletTexture = this.textureLoader.load('assets/space_pirates/SparkParticle.png');
        this.bulletTexture.minFilter = THREE.LinearFilter;
        this.bulletTexture.magFilter = THREE.LinearFilter;
        this.bulletTexture.generateMipmaps = false; // Explicitly disable mipmap generation
    }

    startFiring() {
        this.stopFiring(); // Ensure no multiple intervals running
        //this.fireInterval = setInterval(() => this.fire(), this.config.bullets.fireRate);
    }

    stopFiring() {
        clearInterval(this.fireInterval);
    }

    fire() {
        const bulletSize = this.config.bullets.size;
        const geometry = new THREE.PlaneGeometry(bulletSize.width, bulletSize.height);
        const material = new THREE.MeshBasicMaterial({
            map: this.bulletTexture,
            color: 0x00ff00,
            transparent: true,
            blending: THREE.AdditiveBlending,
            side: THREE.DoubleSide,
            depthWrite: false
        });
        const bulletMesh = new THREE.Mesh(geometry, material);

        for (let i = 0; i < 2; i++) {
            const copy = bulletMesh.clone();
            copy.position.z += (i + 1) * 0.01;
            copy.scale.set(1.3, 0.5, 1);
            bulletMesh.add(copy);
        }

        const spawnPosition = new THREE.Vector3(0, -0.6, 0);
        this.playerRig.localToWorld(spawnPosition);
        bulletMesh.position.copy(spawnPosition);

        const rotationX = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(1, 0, 0), Math.PI / 2);
        const rotationZ = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 0, 1), Math.PI / 2);
        bulletMesh.quaternion.copy(rotationX.multiply(rotationZ));
        bulletMesh.scale.set(20, 0.2, 1);

        const playerVelocity = this.physics.playerBody.velocity;
        const bulletRelativeSpeed = this.config.bullets.speed;
        const velocity = new CANNON.Vec3(
            playerVelocity.x,
            playerVelocity.y,
            playerVelocity.z - bulletRelativeSpeed
        );
        const bulletBody = this.physics.createBullet(bulletMesh, velocity);

        this.scene.add(bulletMesh);
        this.bullets.push({ mesh: bulletMesh, body: bulletBody });
    }

    removeBullet(bulletBody) {
        const index = this.bullets.findIndex(b => b.body.id === bulletBody.id);
        if (index !== -1) {
            const bullet = this.bullets[index];
            this.scene.remove(bullet.mesh);
            this.bullets.splice(index, 1);
        }
    }

    update() {
        this.bullets.forEach(bullet => {
            bullet.mesh.position.copy(bullet.body.position);

            if (bullet.mesh.position.z < this.playerRig.position.z - 150) {
                if (!this.physics.bodiesToRemove.includes(bullet.body)) {
                    this.physics.bodiesToRemove.push(bullet.body);
                }
                this.removeBullet(bullet.body);
            }
        });
    }

    reset() {
        this.stopFiring();
        this.bullets.forEach(bullet => {
            this.scene.remove(bullet.mesh);
            if (!this.physics.bodiesToRemove.includes(bullet.body)) {
                this.physics.bodiesToRemove.push(bullet.body);
            }
        });
        this.bullets = [];
    }
}



class SpacePirates {

    // --- Core Properties ---
    camera;
    scene;
    renderer;
    gameContainer;
    audioListener;
    gameGroup;
    passtroughGroup;
    controllers = [];
    hands = [];
    lastTime = 0;
    frameTimes = [];
    maxFrameSamples = 60; // Average FPS over the last 60 frames
    playerRig;
    moveInput = 0;
    leftMoveAmount = 0;
    rightMoveAmount = 0;
    gameState = GameState.MENU;
    keyLeft = false;
    keyRight = false;
    gamepadMove = 0;
    gamepadJumpPressed = false;
    isJumping = false;
    jumpTime = 0;
    score = 0;
    lives = 3;
    metersTraveled = 0;
    lastSpeedIncreaseMeters = 0;
    speedIncreaseInterval = 2000;
    speedIncreaseAmount = 15;
    currentBiomeIndex = 0;
    scoreText;
    livesText;
    distanceText;
    groundSegments = [];
    groundSegmentSize = 2000;
    numGroundSegments = 4; // One for each biome
    starsContainer = null;

    dirLight;
    skyDome = null;
    wallManager;
    audioManager;
    config = {
        showStats: false,
        physicsDebugger: false,
        lanes: {
            width: 50,
        },
        player: {
            startSpeed: 50,
            forwardSpeed: 50,
            sideMoveSpeed: 20, // units per second
            maxSpeed: 100,
            jumpHeight: 10,
            jumpDuration: 10, // in seconds
        },
        asteroids: {
            startY: -0.5,
            spawnDistance: -200,
        },
        bullets: {
            speed: 50, // Relative to player
            fireRate: 200, // ms
            size: {
                width: 0.1,
                height: 1,
            }
        },
        walls: {
            seed: 12345,
            spawnDistance: -100,
            blockSize: { width: 6, height: 6, depth: 6 },
            gridHeight: 2,
            maxWalls: 4000,
        },
        parallax: {
            starsSpeed: 0.05, // Factor of player Z position
        },
        biomes: [
            { // Biome 0: Default
                distance: 0,
                name: 'Deep Space',
                groundBackgroundColor: '#444444',
                groundLineColor: '#888888',
                wallColor: 0x999999,
                skyColorTop: '#000000',
                skyColorBottom: '#000000',
            },
            { // Biome 1
                distance: 1250,
                name: 'Red Dust Nebula',
                groundBackgroundColor: '#2a5a5a',
                groundLineColor: '#5a9a9a',
                wallColor: 0x999999,
                skyColorTop: '#000000',
                skyColorBottom: '#000000',
            },
            { // Biome 2
                distance: 3250,
                name: 'Violet Veil',
                groundBackgroundColor: '#3a2a5a',
                groundLineColor: '#7a5a9a',
                wallColor: 0x999999,
                skyColorTop: '#000000',
                skyColorBottom: '#000000',
            },
            { // Biome 3
                distance: 5250,
                name: 'Crystalline Expanse',
                groundBackgroundColor: '#2a5a5a',
                groundLineColor: '#5a9a9a',
                wallColor: 0x999999,
                skyColorTop: '#000000',
                skyColorBottom: '#000000',
            }
        ],
    };

    // --- Loaders ---
    textureLoader = new THREE.TextureLoader();
    gltfLoader = new GLTFLoader();
    controllerModelFactory = new XRControllerModelFactory();
    handModelFactory = new XRHandModelFactory();


    // --- Physics ---
    physics; // Instance of SpacePiratesPhysics
    cockpitButtons = [];

    // --- Input Handling ---
    raycaster = new THREE.Raycaster();
    mouse = new THREE.Vector2();
    selectedObject = null; // The physics object being "held" by the mouse


    constructor({ gameContainer, ui2d, leaderboard }) {
        this.leaderboard = leaderboard || null;
        this.gameContainer = gameContainer;
        this.ui2d = {
            container: ui2d,
            score: ui2d.querySelector('#score-2d'),
            lives: ui2d.querySelector('#lives-2d'),
            coins: ui2d.querySelector('#coins-2d'),
            distance: ui2d.querySelector('#distance-2d'),
            gameState: ui2d.querySelector('#game-state-2d'),
            start: ui2d.querySelector('#start-btn-2d'),
            restart: ui2d.querySelector('#restart-btn-2d'),
            sfxMute: ui2d.querySelector('#sfx-mute-btn-2d'),
            sfxIcon: ui2d.querySelector('#sfx-icon-2d'),
        };
        this.init();
    }


    init() {
        this.setupScene();
        this.createPlayerRig();
        this.physics = new SpacePiratesPhysics(this.scene, this.onPlayerHit.bind(this), this.onBulletHit.bind(this), this.config.physicsDebugger);
        this.bulletManager = new BulletManager(this.scene, this.physics, this.config, this.playerRig);
        this.wallManager = new WallManager(this.scene, this.physics, this.config, this.playerRig);
        this.audioManager = new AudioManager();

        this.setupLights();
        this.setupEnvironment();
        this.createCockpitControls();
        this.createInGameUI();
        this.createStatsUI();
        this.setupControls();
        this.setupUI();
        this.loadGround();

        gsap.ticker.lagSmoothing(false);
        gsap.ticker.remove(gsap.updateRoot);


        // --- FIX: Bind the 'this' context for the onResize method ---
        this.onResize = this.onResize.bind(this);
        window.addEventListener('resize', this.onResize);
        this.ui2d.lives.style.display = 'flex';
        this.ui2d.gameState.style.display = 'flex';
        this.ui2d.coins.style.display = 'flex';
        this.ui2d.distance.style.display = 'flex';

        this.ui2d.start.style.display = 'none';

        // Initial resize call
        this.onResize();


    }


    setupUI() {
        this.ui2d.container.style.display = 'block';
        this.updateUI();
    }

    updateUI() {
        this.ui2d.score.textContent = `Score: ${this.config.forwardSpeed}`;
        this.ui2d.lives.textContent = `Lives: ${this.lives}`;
        this.ui2d.distance.textContent = `Meters: ${Math.floor(this.metersTraveled)}`;
        this.ui2d.gameState.textContent = `State: ${this.gameState}`;

        if (this.scoreText) {
            this.scoreText.text = `Speed: ${Math.floor(this.config.player.forwardSpeed)}`;
            this.scoreText.sync();
        }
        if (this.livesText) {
            this.livesText.text = `Lives: ${this.lives}`;
            this.livesText.sync();
        }
        if (this.distanceText) {
            this.distanceText.text = `Meters: ${Math.floor(this.metersTraveled)}`;
            this.distanceText.sync();
        }
    }

    startGame() {
        if (this.gameState === GameState.PLAYING) return;

        this.config.player.forwardSpeed = this.config.player.startSpeed; // Reset speed to initial value
        // Initialize and start the audio on the first playthrough
        if (!this.audioManager.isInitialized) {
            this.audioManager.init().then(() => {
                this.audioManager.startEngine();
            });
        } else {
            this.audioManager.startEngine();
        }
        // Animate fog to expand view distance
        gsap.to(this.scene.fog, {
            far: 800,
            duration: 1, // A bit longer for a smoother effect
            ease: 'power2.inOut'
        });

        this.bulletManager.reset();
        this.wallManager.reset();

        this.playerRig.position.set(0, 0, 0);
        this.physics.playerBody.position.set(0, 0, 0);
        this.physics.playerBody.velocity.set(0, 0, 0);
        this.physics.playerBody.angularVelocity.set(0, 0, 0);
        this.physics.playerBody.quaternion.set(0, 0, 0, 1); // Reset rotation to default

        this.isJumping = false;
        this.jumpTime = 0;
        // Reset ground tiling and biomes
        this.groundSegments.forEach((segment, i) => {
            segment.position.z = (-this.groundSegmentSize / 2) + i * -this.groundSegmentSize;
        });
        this.currentBiomeIndex = 0;
        this.updateBiomeVisuals(this.currentBiomeIndex);
        const initialBiome = this.config.biomes[0];

        this.gameState = GameState.PLAYING;
        this.score = 0;
        this.lives = 3;
        this.metersTraveled = 0;
        this.lastSpeedIncreaseMeters = 0;
        this.bulletManager.startFiring();
        this.wallManager.startSpawning();
        this.updateUI();
    }

    gameOver() {
        if (this.gameState === GameState.GAME_OVER) return;

        // Animate fog to shrink view distance
        gsap.to(this.scene.fog, {
            far: 20,
            duration: 0.5, // Quicker to signal an abrupt end
            ease: 'power3.out'
        });

        this.gameState = GameState.GAME_OVER;
        this.bulletManager.stopFiring();
        this.wallManager.stopSpawning();
        this.updateUI();
        this.audioManager.stopEngine();

        const finalScore = this.score || 0;
        if (this.leaderboard && finalScore > 0) {
            this.leaderboard.submitAndShow(finalScore);
        }
    }

    restartGame() {
        this.gameOver(); // Stop everything
        this.startGame(); // Start fresh
    }

    onPlayerHit(event, type = 'asteroid') {
        if (this.gameState !== GameState.PLAYING) return;

        const body = event.body;

        if (type === 'wall') {
            this.lives = 0;
            this.physics.playerBody.velocity.set(0, 0, 0);
            this.physics.playerBody.angularVelocity.set(0, 0, 0);
            this.physics.playerBody.quaternion.set(0, 0, 0, 1); // Reset rotation
            this.gameOver();
            return;
        }


        this.lives--;
        this.updateUI();

        if (this.lives <= 0) {
            this.gameOver();
        }
    }

    onBulletHit(asteroidBody, bulletBody) {
        if (this.gameState !== GameState.PLAYING) return;

        this.bulletManager.removeBullet(bulletBody);

        this.score += 10;
        this.updateUI();

    }


    createPlayerRig() {
        this.playerRig = new THREE.Group();
        this.playerRig.add(this.camera);
        this.scene.add(this.playerRig);

        this.gltfLoader.load('assets/space_pirates/cockpit.glb', (gltf) => {
            const cockpit = SkeletonUtils.clone(gltf.scene);
            cockpit.position.set(0, -1.29, -0.11);
            cockpit.scale.set(1, 1, 1);
            cockpit.quaternion.setFromEuler(new THREE.Euler(0, Math.PI, 0));
            cockpit.traverse((child) => {
                if (child.isMesh) {
                    child.receiveShadow = true;
                }
            });
            this.playerRig.add(cockpit);

        });


    }

    // --- Setup Methods ---


    setupScene() {
        this.scene = new THREE.Scene();
        // Start with the 'game over' fog setting
        this.scene.fog = new THREE.Fog(0x000000, 1, 20);
        this.camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 2000);
        this.camera.position.set(0, 0, 0.1);


        this.audioListener = new THREE.AudioListener();
        this.camera.add(this.audioListener);


        this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
        this.renderer.shadowMap.enabled = true;
        this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
        this.renderer.setClearColor(0x0000, 0);
        this.renderer.setSize(window.innerWidth, window.innerHeight);
        this.renderer.setPixelRatio(window.devicePixelRatio);
        this.renderer.xr.enabled = true;
        this.renderer.setAnimationLoop(this.animation.bind(this));
        this.gameContainer.appendChild(this.renderer.domElement);


        this.gameGroup = new THREE.Group();
        this.scene.add(this.gameGroup);


        this.passtroughGroup = new THREE.Group();
        this.scene.add(this.passtroughGroup);
    }


    setupLights() {
        const ambientLight = new THREE.AmbientLight(0x404050, 1);
        this.scene.add(ambientLight);

        const dirLight = new THREE.DirectionalLight(0xfff4d6, 2);
        dirLight.position.set(10, 20, 5);
        dirLight.castShadow = true;

        // Configure shadow properties
        const desktopShadowSize = 100;
        dirLight.shadow.camera.top = desktopShadowSize;
        dirLight.shadow.camera.bottom = -desktopShadowSize;
        dirLight.shadow.camera.left = -desktopShadowSize;
        dirLight.shadow.camera.right = desktopShadowSize;


        dirLight.shadow.camera.near = 0.5;
        dirLight.shadow.camera.far = 100;
        dirLight.shadow.mapSize.width = 2048;
        dirLight.shadow.mapSize.height = 2048;

        this.dirLight = dirLight;
        this.scene.add(dirLight);
    }


    setupEnvironment() {
        this.createSkyDome();
        this.createParallaxLayers();
    }


    setupControls() {
        this.setupXrControls();
        this.setupMouseAndTouchControls();
        this.setupKeyboardControls();
        this.setupGamepadControls();
    }

    setupKeyboardControls() {
        // Bind 'this' to the event handlers to ensure they have the correct context
        this.onKeyDown = this.onKeyDown.bind(this);
        this.onKeyUp = this.onKeyUp.bind(this);

        window.addEventListener('keydown', this.onKeyDown);
        window.addEventListener('keyup', this.onKeyUp);
    }

    onKeyDown(event) {
        switch (event.key.toLowerCase()) {
            case 'a':
                this.keyLeft = true;
                break;
            case 'd':
                this.keyRight = true;
                break;
            case ' ': // Spacebar
                this.jump();
                break;
        }
    }

    onKeyUp(event) {
        switch (event.key.toLowerCase()) {
            case 'a':
                this.keyLeft = false;
                break;
            case 'd':
                this.keyRight = false;
                break;
            case 'r':
                this.gameOver();
        }
    }

    setupGamepadControls() {
        window.addEventListener('gamepadconnected', (e) => {
            console.log(`Gamepad connected at index ${e.gamepad.index}: ${e.gamepad.id}.`);
        });
        window.addEventListener('gamepaddisconnected', (e) => {
            console.log(`Gamepad disconnected from index ${e.gamepad.index}: ${e.gamepad.id}.`);
        });
    }

    handleGamepadInput() {
        const gamepads = navigator.getGamepads();
        if (!gamepads || !gamepads[0]) {
            this.gamepadMove = 0;
            return;
        }

        const gamepad = gamepads[0]; // Use the first connected gamepad

        // --- Movement (Left Analog Stick) ---
        const stickX = gamepad.axes[0]; // Axis 0 is left stick horizontal
        const deadzone = 0.2;
        if (Math.abs(stickX) > deadzone) {
            this.gamepadMove = stickX;
        } else {
            this.gamepadMove = 0;
        }

        // --- Action Button (A on Xbox, X on PlayStation - Button 0) ---
        // User requested "X for start//jump"
        const actionButton = gamepad.buttons[0];
        if (actionButton && actionButton.pressed) {
            if (!this.gamepadJumpPressed) { // Edge detection
                if (this.gameState === GameState.PLAYING) {
                    this.jump();
                } else if (this.gameState === GameState.MENU || this.gameState === GameState.GAME_OVER) {
                    this.startGame();
                }
            }
            this.gamepadJumpPressed = true;
        } else {
            this.gamepadJumpPressed = false;
        }
    }


    // --- Cockpit Creation ---


    createCockpitControls() {
        // Start/Restart Button

        // const startButton = this.createCockpitButton(
        //     new THREE.Vector3(0, -0.3, -0.6),
        //     new THREE.Quaternion(),
        //     this.playerRig,
        //     () => {
        //         if (this.gameState === GameState.MENU || this.gameState === GameState.GAME_OVER) {
        //             this.startGame();
        //         }
        //     }
        // );
        // this.cockpitButtons.push(startButton);


        const movementButtonSize = { height: 0.05 };

        const leftButton = this.createCockpitButton(
            new THREE.Vector3(-0.12, -0.32, -0.36), new THREE.Quaternion(), this.playerRig,
            null, (pressValue) => { this.leftMoveAmount = pressValue; }, () => { this.leftMoveAmount = 0; },
            { width: 0.08, height: 0.08, depth: 0.06 }
        );
        this.cockpitButtons.push(leftButton);

        const rightButton = this.createCockpitButton(
            new THREE.Vector3(0.12, -0.32, -0.36), new THREE.Quaternion(), this.playerRig,
            null, (pressValue) => { this.rightMoveAmount = pressValue; }, () => { this.rightMoveAmount = 0; },
            { width: 0.08, height: 0.08, depth: 0.06 }
        );
        this.cockpitButtons.push(rightButton);

        const jumpButton = this.createCockpitButton(
            new THREE.Vector3(0, -0.3, -0.45), new THREE.Quaternion(), this.playerRig,
            () => {
                if (this.gameState === GameState.PLAYING) {
                    this.jump();
                } else if (this.gameState === GameState.MENU || this.gameState === GameState.GAME_OVER) {
                    this.startGame();
                }
            }
        );
        this.cockpitButtons.push(jumpButton);

    }


    jump() {
        if (!this.isJumping && this.gameState === GameState.PLAYING) {
            this.isJumping = true;
            this.audioManager.playJumpSound(); // And also here
        }
    }
    createInGameUI() {
        // Container for the UI elements
        const uiContainer = new THREE.Group();
        uiContainer.position.set(0, -0.2, -0.55); // Position it above the buttons
        uiContainer.rotation.x = -Math.PI / 2.5; // Lay it flat, tilted slightly towards the player
        this.playerRig.add(uiContainer);

        const textStyle = {
            font: 'assets/duck_hunt/SpaceMono-Bold.ttf',
            fontSize: 0.018,
            color: 0x00ff00, // Green color for stats
            anchorX: 'left',
            anchorY: 'top',
            textAlign: 'left'
        };
        // Score Text
        this.scoreText = new Text();
        this.scoreText.text = 'Score: 0';
        Object.assign(this.scoreText, textStyle);
        this.scoreText.position.set(0, -0.04, 0);
        this.scoreText.sync(); // Add this line
        uiContainer.add(this.scoreText);

        // Lives Text
        // this.livesText = new Text();
        // this.livesText.text = 'Lives: 3';
        // Object.assign(this.livesText, textStyle);
        // this.livesText.position.set(0, 0, 0);
        // this.livesText.sync(); // Add this line
        // uiContainer.add(this.livesText);

        // Distance Text
        this.distanceText = new Text();
        this.distanceText.text = 'Meters: 0';
        Object.assign(this.distanceText, textStyle);
        this.distanceText.position.set(0, 0, 0);
        this.distanceText.sync(); // Add this line
        uiContainer.add(this.distanceText);

        uiContainer.position.set(-0.05, -0.1, -0.7); // Position it top-left
        uiContainer.rotation.x = -Math.PI / 4; // Less aggressive tilt
    }

    createStatsUI() {
        if (!this.config.showStats) return;

        // Container for the UI elements
        const statsContainer = new THREE.Group();
        statsContainer.position.set(-0.05, -0.1, -0.7); // Position it top-left
        statsContainer.rotation.x = -Math.PI / 4; // Less aggressive tilt
        this.playerRig.add(statsContainer);

        const textStyle = {
            font: 'assets/duck_hunt/SpaceMono-Bold.ttf',
            fontSize: 0.018,
            color: 0x00ff00, // Green color for stats
            anchorX: 'left',
            anchorY: 'top',
            textAlign: 'left'
        };

        this.statsText = new Text();
        this.statsText.text = 'Initializing...';
        Object.assign(this.statsText, textStyle);
        this.statsText.position.set(0, 0, 0); // Position relative to container
        this.statsText.sync();
        statsContainer.add(this.statsText);
    }

    createCockpitButton(pos, rot, parent, onClick, onHold, onRelease, buttonSize = {}) {
        const size = {
            width: buttonSize.width || 0.075,
            height: buttonSize.height || 0.025,
            depth: buttonSize.depth || 0.075
        };
        const baseHeight = 0.025;
        const sunkDepth = 0.00625; // How much the button is sunk into the base at rest

        // --- Button Base (Visual) ---
        const baseGeometry = new THREE.BoxGeometry(0.1, baseHeight, 0.1);
        const baseMaterial = new THREE.MeshStandardMaterial({ color: 0x333333 });
        const buttonBaseMesh = new THREE.Mesh(baseGeometry, baseMaterial);
        buttonBaseMesh.position.copy(pos);
        buttonBaseMesh.quaternion.copy(rot);
        parent.add(buttonBaseMesh);


        // --- Pushable Button (Visual) ---
        const buttonGeometry = new THREE.BoxGeometry(size.width, size.height, size.depth);
        const buttonMaterial = new THREE.MeshStandardMaterial({ color: 0xff0000 });
        const buttonMesh = new THREE.Mesh(buttonGeometry, buttonMaterial);


        const initialButtonYOffset = (baseHeight / 2) - sunkDepth + (size.height / 2);
        const initialButtonPosition = new THREE.Vector3(pos.x, pos.y + initialButtonYOffset, pos.z);
        buttonMesh.position.copy(initialButtonPosition);
        buttonMesh.quaternion.copy(rot);
        parent.add(buttonMesh);
        buttonMesh.receiveShadow = true;

        // --- Create Physics Representation ---
        const physicsObject = this.physics.createCockpitButtonPhysics(buttonMesh, pos, rot, onClick, onHold, onRelease, size, initialButtonYOffset, sunkDepth);
        return physicsObject;
    }


    // --- Input Handling ---


    setupMouseAndTouchControls() {
        const domElement = this.renderer.domElement;
        domElement.addEventListener('mousedown', this.onInputDown.bind(this));
        domElement.addEventListener('touchstart', this.onInputDown.bind(this), { passive: false });
        domElement.addEventListener('mouseup', this.onInputUp.bind(this));
        domElement.addEventListener('touchend', this.onInputUp.bind(this));
    }


    onInputDown(event) {
        event.preventDefault();
        const coords = this.getCanvasCoordinates(event);
        this.mouse.set(coords.x, coords.y);
        this.raycaster.setFromCamera(this.mouse, this.camera);
        const intersects = this.raycaster.intersectObjects(this.physics.physicsObjects.map(p => p.mesh), true);

        if (intersects.length > 0) {
            const clickedMesh = intersects[0].object;
            const physicsObject = this.physics.physicsObjects.find(p => p.mesh.children.includes(clickedMesh) || p.mesh === clickedMesh);
            if (physicsObject && physicsObject.type === 'button') {
                this.selectedObject = physicsObject;
                this.selectedObject.isMousePressed = true;
            }
        }
    }


    onInputUp() {
        if (this.selectedObject) {
            this.selectedObject.isMousePressed = false;
            this.selectedObject = null;
        }
    }


    getCanvasCoordinates(event) {
        const rect = this.renderer.domElement.getBoundingClientRect();
        const touch = event.changedTouches ? event.changedTouches[0] : event;
        return {
            x: ((touch.clientX - rect.left) / rect.width) * 2 - 1,
            y: -((touch.clientY - rect.top) / rect.height) * 2 + 1,
        };
    }


    // --- Environment ---


    createSkyDome() {
        const skyGeometry = new THREE.SphereGeometry(1900, 32, 32);
        const canvas = document.createElement('canvas');
        canvas.width = 512;
        canvas.height = 512;
        const context = canvas.getContext('2d');
        const gradient = context.createLinearGradient(0, 0, 0, canvas.height);
        gradient.addColorStop(0, '#000000ff');
        gradient.addColorStop(1, '#000000ff');
        context.fillStyle = gradient;
        context.fillRect(0, 0, canvas.width, canvas.height);


        const skyTexture = new THREE.CanvasTexture(canvas);
        const skyMaterial = new THREE.MeshBasicMaterial({ map: skyTexture, side: THREE.BackSide, fog: false });
        this.skyDome = new THREE.Mesh(skyGeometry, skyMaterial);
        this.gameGroup.add(this.skyDome);
    }

    updateSkyDome(topColor, bottomColor) {
        if (!this.skyDome) return;

        const canvas = this.skyDome.material.map.image;
        const context = canvas.getContext('2d');

        const gradient = context.createLinearGradient(0, 0, 0, canvas.height);
        gradient.addColorStop(0, topColor);
        gradient.addColorStop(1, bottomColor);

        context.fillStyle = gradient;
        context.fillRect(0, 0, canvas.width, canvas.height);

        this.skyDome.material.map.needsUpdate = true;
    }

    createParallaxLayers() {
        // Stars Layer
        this.starsContainer = new THREE.Group();
        this.scene.add(this.starsContainer);

        const starVertices = [];
        const starMinDist = 800, starMaxDist = 1000;
        for (let i = 0; i < 10000; i++) {
            const vec = new THREE.Vector3(
                THREE.MathUtils.randFloatSpread(2),
                THREE.MathUtils.randFloatSpread(2),
                THREE.MathUtils.randFloatSpread(2)
            ).normalize().multiplyScalar(THREE.MathUtils.randFloat(starMinDist, starMaxDist));
            starVertices.push(vec.x, vec.y, vec.z);
        }
        const starGeometry = new THREE.BufferGeometry();
        starGeometry.setAttribute("position", new THREE.Float32BufferAttribute(starVertices, 3));
        const starMaterial = new THREE.PointsMaterial({ color: 0xffffff, size: 1.5, fog: false });
        const stars = new THREE.Points(starGeometry, starMaterial);
        this.starsContainer.add(stars);
    }



    createGridTexture(backgroundColor, lineColor) {
        const canvas = document.createElement('canvas');
        canvas.width = 256;
        canvas.height = 256;
        const context = canvas.getContext('2d');
        context.fillStyle = backgroundColor;
        context.fillRect(0, 0, 256, 256);
        context.strokeStyle = lineColor;
        context.lineWidth = 1; // Thinner lines for a more subtle effect
        context.globalAlpha = 0.4; // Make lines more transparent
        for (let i = 0; i < 256; i += 32) {
            context.beginPath();
            context.moveTo(i, 0);
            context.lineTo(i, 256);
            context.stroke();
            context.beginPath();
            context.moveTo(0, i);
            context.lineTo(256, i);
            context.stroke();
        }
        context.globalAlpha = 1.0; // Reset alpha
        return new THREE.CanvasTexture(canvas);
    }

    loadGround() {
        const groundGeometry = new THREE.PlaneGeometry(2000, this.groundSegmentSize);
        const groundPhysicsMaterial = new CANNON.Material('ground');
        const groundBody = new CANNON.Body({
            mass: 0, // static
            shape: new CANNON.Plane(),
            material: groundPhysicsMaterial
        });
        groundBody.quaternion.setFromEuler(-Math.PI / 2, 0, 0);
        groundBody.position.set(0, -2, 0);
        this.physics.physicsWorld.addBody(groundBody);

        this.config.biomes.forEach((biome, i) => {
            const groundMaterial = new THREE.MeshPhysicalMaterial({
                color: 0xffffff, // Add a bright base color
                metalness: 0.8,  // Reduce metalness
                roughness: 0.7,  // Increase roughness for more diffuse reflections
                clearcoat: 1.0,
                clearcoatRoughness: 0.9,
                reflectivity: 0.9, // Slightly increase reflectivity
            });
            const groundTexture = this.createGridTexture(biome.groundBackgroundColor, biome.groundLineColor);
            groundTexture.wrapS = THREE.RepeatWrapping;
            groundTexture.wrapT = THREE.RepeatWrapping;
            groundTexture.repeat.set(25, 12.5); // 2000x1000 plane
            groundTexture.anisotropy = this.renderer.capabilities.getMaxAnisotropy();
            groundMaterial.map = groundTexture;

            const groundPlane = new THREE.Mesh(groundGeometry, groundMaterial);
            groundPlane.rotation.x = -Math.PI / 2;
            // Position the center of the plane, and lower each subsequent tile slightly to prevent z-fighting
            const zPos = -(i * this.groundSegmentSize) - (this.groundSegmentSize / 2);
            groundPlane.position.set(0, -2.01 - i * 0.001, zPos);
            groundPlane.receiveShadow = true;
            this.scene.add(groundPlane);
            this.groundSegments.push(groundPlane);
        });
    }

    // --- Core Loop and Lifecycle ---


    onResize() {
        // Now 'this' correctly refers to the SpacePirates instance
        const { clientWidth, clientHeight } = this.gameContainer;
        this.renderer.setSize(clientWidth, clientHeight);
        this.camera.aspect = clientWidth / clientHeight;
        this.camera.updateProjectionMatrix();
    }


    dispose() {
        this.renderer.setAnimationLoop(null);
        // We can now correctly remove the listener because 'this.onResize' refers to the bound function
        window.removeEventListener('resize', this.onResize);

        window.removeEventListener('keydown', this.onKeyDown);
        window.removeEventListener('keyup', this.onKeyUp);

        if (this.audioManager && typeof this.audioManager.dispose === 'function') {
            this.audioManager.dispose();
        }

        if (this.scoreText) this.scoreText.dispose();
        if (this.livesText) this.livesText.dispose();
        if (this.distanceText) this.distanceText.dispose();
        // Add more cleanup here (e.g., dispose geometries, materials)
    }

    getBiomeIndexForDistance(distance) {
        const reversedBiomes = this.config.biomes.slice().reverse();
        const biomeIndexInReversed = reversedBiomes.findIndex(b => distance >= b.distance);
        if (biomeIndexInReversed === -1) {
            return 0; // Default to the first biome
        }
        return this.config.biomes.length - 1 - biomeIndexInReversed;
    }

    updateBiomeVisuals(biomeIndex) {
        const biome = this.config.biomes[biomeIndex];
        if (!biome) return;

        console.log(`Switching to Biome ${biomeIndex}: ${biome.name}`);
        console.log('playe z position:', this.physics.playerBody.position.z);
        // Update Sky
        this.updateSkyDome(biome.skyColorTop, biome.skyColorBottom);

        // Ground texture is now handled in the animation loop when segments are recycled

        // Update Walls
        this.wallManager.updateWallColor(biome.wallColor);
        this.wallManager.setBiome(biomeIndex);


    }

    increasePlayerSpeed() {
        const newSpeed = this.config.player.forwardSpeed + this.speedIncreaseAmount;

        console.log(`Increasing speed to ${newSpeed} at ${Math.floor(this.metersTraveled)}m`);

        gsap.to(this.config.player, {
            forwardSpeed: newSpeed, // The target value
            duration: 1,            // The animation duration in seconds
            ease: 'power2.inOut'    // A smooth ease for the transition
        });
    }

    updateStats() {
        if (!this.statsText) return;

        const totalFrameTime = this.frameTimes.reduce((acc, dt) => acc + dt, 0);
        const averageFrameTime = totalFrameTime / this.frameTimes.length;
        const fps = (1 / averageFrameTime).toFixed(0);

        const walls = this.wallManager.walls.length;
        const triangles = this.renderer.info.render.triangles;
        const drawCalls = this.renderer.info.render.calls;

        this.statsText.text = `FPS: ${fps}\nWalls: ${walls}\nTris: ${triangles}\nCalls: ${drawCalls}`;
        this.statsText.sync();
    }


    handleControllerButtonInteraction() {
        // First, reset the XR press state for all buttons.
        // This ensures buttons are released when the controller moves away.
        for (const button of this.cockpitButtons) {
            button.isBeingPressedByXR = false;
        }

        const interactionThresholdSq = 0.05 * 0.05; // 5cm squared

        for (const controller of this.controllers) {
            // Skip this controller if it's not grabbing or doesn't have a physics body
            if (!controller.userData.isGrabbing || !controller.userData.physicsBody) {
                continue;
            }

            let closestButton = null;
            let minDistanceSq = interactionThresholdSq;

            const controllerPos = controller.userData.physicsBody.position;

            // Find the closest button within the threshold
            for (const button of this.cockpitButtons) {
                const buttonPos = button.body.position;
                const distanceSq = controllerPos.distanceSquared(buttonPos);

                if (distanceSq < minDistanceSq) {
                    minDistanceSq = distanceSq;
                    closestButton = button;
                }
            }

            // If we found a button close enough, flag it to be pressed.
            if (closestButton) {
                closestButton.isBeingPressedByXR = true;
            }
        }
    }

    jumpUpdate(deltaTime) {
        if (this.isJumping) {
            this.jumpTime += deltaTime;
            const jumpProgress = this.jumpTime / (this.config.player.jumpDuration / 2);

            if (jumpProgress < 1) {
                // Going up
                this.physics.playerBody.velocity.y = this.config.player.jumpHeight * (1 - jumpProgress);
            } else {
                // Going down
                this.physics.playerBody.velocity.y = -this.config.player.jumpHeight * (jumpProgress - 1);
            }

            if (this.jumpTime >= this.config.player.jumpDuration) {
                this.isJumping = false;
                this.jumpTime = 0;
                this.physics.playerBody.velocity.y = 0;
                this.audioManager.playLandSound(); // Play landing sound here

            }
        } else {
            this.physics.playerBody.velocity.y = 0; // Ensure no vertical movement
        }
    }
    animation(time, frame) {
        const deltaTime = this.lastTime > 0 ? (time - this.lastTime) / 1000 : 1 / 60;
        this.lastTime = time;

        // Store frame time for averaging
        this.frameTimes.push(deltaTime);
        if (this.frameTimes.length > this.maxFrameSamples) {
            this.frameTimes.shift();
        }

        this.handleGamepadInput();
        this.handleControllerButtonInteraction();
        const buttonMove = this.rightMoveAmount - this.leftMoveAmount;
        const keyMove = (this.keyRight ? 1 : 0) - (this.keyLeft ? 1 : 0);
        const totalMove = buttonMove + keyMove + this.gamepadMove;
        this.moveInput = Math.max(-1, Math.min(1, totalMove));

        if (this.gameState === GameState.PLAYING) {
            // --- Update Audio ---
            if (this.audioManager.isInitialized) {
                // Normalize player speed to a 0-1 range for the audio manager
                const currentForwardSpeed = Math.abs(this.physics.playerBody.velocity.z);
                const maxSpeed = this.config.player.maxSpeed;
                const normalizedSpeed = currentForwardSpeed / maxSpeed;
                this.audioManager.updateEngineSpeed(normalizedSpeed);
                this.audioManager.updateTilt(this.moveInput);
            }
        } else {
            // Stop the engine sound when not playing
            if (this.audioManager.isInitialized) {
                this.audioManager.stopEngine();
            }
        }


        if (this.gameState === GameState.PLAYING) {
            // Set velocity for the dynamic body
            this.physics.playerBody.velocity.z = -this.config.player.forwardSpeed;
            this.physics.playerBody.velocity.x = this.moveInput * this.config.player.sideMoveSpeed;
            this.jumpUpdate(deltaTime);



            this.metersTraveled = -this.physics.playerBody.position.z;

            if (this.metersTraveled > this.lastSpeedIncreaseMeters + this.speedIncreaseInterval) {
                this.increasePlayerSpeed();
                this.lastSpeedIncreaseMeters += this.speedIncreaseInterval;
            }

            // Cockpit tilt animation
            const tiltAngle = 10 * (Math.PI / 180);
            gsap.to(this.playerRig.rotation, {
                z: -this.moveInput * tiltAngle,
                duration: 0.3,
                ease: 'power2.out'
            });

            this.metersTraveled = -this.physics.playerBody.position.z;

            // Biome switching logic
            const newBiomeIndex = this.getBiomeIndexForDistance(this.metersTraveled);
            if (newBiomeIndex !== this.currentBiomeIndex) {
                console.log(`[BIOME] Player crossed threshold. New biome index: ${newBiomeIndex}, Meters: ${this.metersTraveled}`);
                this.currentBiomeIndex = newBiomeIndex;
                this.updateBiomeVisuals(this.currentBiomeIndex);
            }

            this.dirLight.position.z = this.playerRig.position.z + 5; // Adjust offset as needed
            this.dirLight.position.x = this.playerRig.position.x;
            this.dirLight.target.position.z = this.playerRig.position.z; // Ensure the light targets the player's current Z
            this.dirLight.target.position.x = this.playerRig.position.x;
            this.dirLight.target.updateMatrixWorld(); // Important for the light's target to update its position
            this.updateUI();
        } else {
            // Stop movement when not playing
            this.physics.playerBody.velocity.set(0, 0, 0);
        }

        this.physics.update(deltaTime);

        // Update player rig from physics body AFTER physics step
        this.playerRig.position.copy(this.physics.playerBody.position);
        this.playerRig.quaternion.copy(this.physics.playerBody.quaternion);

        // Apply the visual offset to the player rig, not the physics body
        this.playerRig.position.z += 1;
        this.playerRig.position.y += 0.7;

        // The managers no longer need a speed parameter
        this.bulletManager.update();
        this.wallManager.update(deltaTime);

        const playerZ = this.playerRig.position.z;

        // Culling for static ground segments
        const CULLING_DISTANCE = 3500; // Should be larger than fog distance
        this.groundSegments.forEach(segment => {
            const distance = Math.abs(playerZ - segment.position.z);
            segment.visible = distance < CULLING_DISTANCE;
        });


        if (this.starsContainer) {
            this.starsContainer.position.z = playerZ;
        }

        if (this.skyDome) {
            this.skyDome.position.z = playerZ;
        }

        const cameraWorldPosition = new THREE.Vector3();
        this.camera.getWorldPosition(cameraWorldPosition);

        this.updateControllers();
        this.updateMeshes();

        gsap.updateRoot(time / 1000);

        if (this.config.showStats) {
            this.updateStats();
        }

        this.renderer.render(this.scene, this.camera);
    }


    updateControllers() {
        this.controllers.forEach(controller => {
            if (controller.userData.physicsBody) {
                // Apply a small forward offset to the physics body to better align with the hand
                const offset = new THREE.Vector3(0, 0, -0.05);
                offset.applyQuaternion(controller.quaternion);
                controller.userData.physicsBody.position.copy(controller.position);
                controller.userData.physicsBody.quaternion.copy(controller.quaternion);
            }
        });
    }


    updateMeshes() {
        // Update all physics-driven meshes EXCEPT the player rig, which is handled directly
        this.physics.physicsObjects.forEach(obj => {
            obj.mesh.position.copy(obj.body.position);
            obj.mesh.quaternion.copy(obj.body.quaternion);
        });
    }


    onEnterXR() 
    { 

    }
    onExitXR() { 


    }


    // --- XR Specifics ---


    setupXrControls() {
        for (let i = 0; i < 2; i++) {
            // Controllers
            const controller = this.renderer.xr.getController(i);
            this.playerRig.add(controller);
            this.controllers.push(controller);

            // No select events needed, physics handles interaction
            controller.addEventListener('connected', this.onXrControllerConnected.bind(this));
            controller.addEventListener('disconnected', this.onXrControllerDisconnected.bind(this));

            controller.addEventListener('selectstart', () => {
                // When the trigger is pressed, set our flag to true
                controller.userData.isGrabbing = true;
            });

            controller.addEventListener('selectend', () => {
                // When the trigger is released, set our flag to false
                controller.userData.isGrabbing = false;
            });
            // Controller Models
            const grip = this.renderer.xr.getControllerGrip(i);
            grip.add(this.controllerModelFactory.createControllerModel(grip));
            this.playerRig.add(grip);

            // Hand Models
            const hand = this.renderer.xr.getHand(i);
            hand.add(this.handModelFactory.createHandModel(hand, 'mesh'));
            this.playerRig.add(hand);
            this.hands.push(hand);
        }
    }

    onXrControllerConnected(event) {
        const controller = event.target;
        if (controller.userData.physicsBody) {
            this.physics.removeControllerPhysicsBody(controller.userData.physicsBody);
        }
        controller.userData.physicsBody = this.physics.createControllerPhysicsBody();
    }


    onXrControllerDisconnected(event) {
        const controller = event.target;
        this.physics.removeControllerPhysicsBody(controller.userData.physicsBody);
        controller.userData.physicsBody = null;
    }
}
export default SpacePirates;